// src/components/Companies/Index.js
import React, { useEffect, useRef, useState } from "react";
import { useCompanies } from "../../hooks/companies";
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useMediaQuery,
} from "@mui/material";
import "./Index.css";
import { useTheme } from "@mui/material/styles";
import Avatar from "@mui/material/Avatar";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import upArrowGreen from "../../images/svg/upArrowGreen.svg";
import downArrowGrey from "../../images/svg/downArrowGrey.svg";
import SearchGrey from "../../images/svg/SearchGrey.svg";
import { useNavigate } from "react-router-dom";
import { leadsAPI } from "../../api/leads";
import { Link } from "@mui/icons-material";
import * as amplitude from '@amplitude/analytics-browser';
import { useAuth } from "../../context/AuthContext";
import { ArrowRight } from "lucide-react";
import CreatableSingleSelect from "../CustomSelects/CreatableSingleSelect";
import SearchableMutliSelect from "../CustomSelects/SearchableMutliSelect";

const LeadsTable = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [searchBy, setSearchBy] = useState("");
    const [displayData, setDisplayData] = useState([]);
    const [leadsData, setLeadsData] = useState([]);
    const [sortField, setSortField] = useState("");
    const [sortDirection, setSortDirection] = useState("asc");
    const [error, setError] = useState(null);

    const [listNameOptions, setListNameOptions] = useState([]);
    const [selectedListNames, setSelectedListNames] = useState(null);

    const { user } = useAuth();


    const fetchLeadsData = async () => {
        try {
            const response = await leadsAPI.fetchAllLeads();
            if (response?.statusCode === 200) {
                const updatedData = response?.data?.map(row => ({
                    ...row,
                    createdAt: row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '-',
                    name: `${row.firstName || ''} ${row.lastName || ''}`.trim() // Handles missing firstName/lastName
                })) || [];

                setLeadsData(updatedData);
                setDisplayData(updatedData);
            }
        } catch (err) {
            setLeadsData([]);
            setDisplayData([]);
            setError(err.message);
            console.error('Error fetching all leads:', err);
        } finally {
        }
    };


    const sortData = (data, field, direction) => {
        return [...data].sort((a, b) => {
            let compareA = a[field];
            let compareB = b[field];

            if (field === 'dealValue') {
                compareA = compareA === '-' ? 0 : parseInt(compareA.replace(/[^0-9]/g, ''));
                compareB = compareB === '-' ? 0 : parseInt(compareB.replace(/[^0-9]/g, ''));
            }

            // Handle date sorting for lastInteraction
            if (field === 'lastInteraction') {
                compareA = compareA === '-' ? new Date(0) : new Date(compareA);
                compareB = compareB === '-' ? new Date(0) : new Date(compareB);
            }

            // Sort by listDetails.label (if field is listDetails)
            if (field === 'listDetails') {
                compareA = compareA?.label?.toLowerCase() || "";
                compareB = compareB?.label?.toLowerCase() || "";
            }

            if (compareA < compareB) return direction === 'asc' ? -1 : 1;
            if (compareA > compareB) return direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    useEffect(() => {
        if (searchBy) {
            let filteredData = leadsData.filter((value) =>
                value?.name?.toLowerCase().includes(searchBy.toLowerCase())
            );
            setDisplayData(filteredData);
        } else {
            setDisplayData(leadsData);
        }

    }, [searchBy]);

    const handleSort = (field) => {
        const newDirection = sortField === field && sortDirection === "asc" ? "desc" : "asc";
        setSortField(field);
        setSortDirection(newDirection);
        const sortedData = sortData(displayData, field, newDirection);
        setDisplayData(sortedData);
    };

    useEffect(() => {
        fetchLeadsData();
    }, []);
    const [openTooltipIndex, setOpenTooltipIndex] = useState(null);

    const getLeadsGroups = async () => {
        try {
            const response = await leadsAPI.getLeadGroups();
            if (response.statusCode === 200) {
                setListNameOptions(response?.data || []);
            }
        } catch (error) {
            console.error('Error in getting leads groups:', error);
            setListNameOptions([]);
        }
    };

    useEffect(() => {
        getLeadsGroups();
    }, []);


    const selectedNewOptionHandler = (value) => {
        setSelectedListNames(value || []);
    }

    useEffect(() => {
        if (!selectedListNames || selectedListNames.length === 0) {
            setDisplayData(leadsData); // Clear table when no selection
            setSearchBy("");
        } else {
            setDisplayData(
                leadsData.filter((lead) =>
                    selectedListNames.some((list) => list._id === lead.listId)
                )
            );
            setSearchBy("");
        }
    }, [selectedListNames]);

    if (error) {
        return (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: "50vh" }}>
                <Typography color="error">Error: {error}</Typography>
            </Box>
        );
    }



    const ExpandableTableCell = ({ content, index, openTooltipIndex, setOpenTooltipIndex, minWidth = 110 }) => {
        const tooltipRef = useRef(null);
        const cellRef = useRef(null);

        const handleClick = (e) => {
            // Toggle tooltip
            setOpenTooltipIndex(openTooltipIndex === index ? null : index);
            
            // Copy content to clipboard
            if (content) {
                console.log(`Copying to clipboard:`, content);
                navigator.clipboard.writeText(content)
                    .then(() => {
                        console.log(`Copy successful`);
                    })
                    .catch(err => {
                        console.error(`Failed to copy text:`, err);
                    });
            }
        };

        useEffect(() => {
            const handleClickOutside = (event) => {
                if (tooltipRef.current && !tooltipRef.current.contains(event.target) && 
                    cellRef.current && !cellRef.current.contains(event.target)) {
                    setOpenTooltipIndex(null);
                }
            };
            document.addEventListener("mousedown", handleClickOutside);
            return () => document.removeEventListener("mousedown", handleClickOutside);
        }, []);

        return (
            <TableCell className="relative deal-value-new" ref={cellRef}>
                <Box 
                    sx={{ minWidth, maxWidth: 200, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontWeight: 600, cursor: "pointer" }}
                    onClick={handleClick}
                >
                    {content || '--'}
                </Box>

                {openTooltipIndex === index && (
                    <div
                        ref={tooltipRef}
                        className="absolute left-0 top-full mt-1 bg-white shadow-lg border p-2 rounded-lg z-10 w-auto max-w-xs"
                    >
                        <div className="text-green-600 font-medium text-sm mb-1 flex items-center border-b pb-1">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Copied
                        </div>
                        <div className="mt-1">{content}</div>
                    </div>
                )}
            </TableCell>
        );
    };


    const handleProfileClick = ({ userId, profileUrl, leadId }) => {
        amplitude.track('Profile URL Clicked', {
            userId,
            profileUrl,
            leadId
        });
        console.log(`amplitude works`)
    };



    return (
        <Box sx={{ p: 2, minHeight: "100vh" }}>
            <Typography
                variant={isMobile ? "h6" : "h5"}
                fontWeight="bold"
                sx={{ mb: 3 }}
                className="companies-main-heading"
            >
                Lead List
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} lg={4} md={6}>
                    <Card sx={{ backgroundColor: "#fff", borderRadius: "12px" }} className="card-total-companies">
                        <CardContent>
                            <Typography variant="body1" className="card-total-companies-pera">
                                Total Leads
                            </Typography>
                            <Grid container spacing={1} alignItems="center">
                                <Grid item>
                                    <Typography
                                        variant={isMobile ? "h5" : "h4"}
                                        className="card-total-companies-vs-count"
                                    >
                                        {leadsData?.length || 0}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }} marginLeft={0} display="flex" justifyContent="space-between" alignItems="end" width="100%">
                <Grid item xs={6} lg={4} md={6} sx={{ border: 0, paddingLeft: `0px !important` }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            border: "1px solid #E5E7EB",
                            borderRadius: "16px",
                            padding: "12px 12px",
                            backgroundColor: "#FFFFFF",
                            boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                        }}
                    >
                        <img
                            src={SearchGrey}
                            alt="🔍"
                            style={{ marginRight: "8px", width: "16px", height: "16px" }}
                        />
                        <input
                            type="text"
                            placeholder="Search..."
                            style={{
                                border: "none",
                                outline: "none",
                                fontSize: "14px",
                                color: "#6B7280",
                                width: "100%",
                            }}
                            value={searchBy}
                            onChange={(e) => setSearchBy(e.target.value)}
                        />
                    </Box>
                </Grid>
                <Grid item xs={6} lg={4} md={6} sx={{ border: 0, paddingLeft: `0px !important` }}>
                    <SearchableMutliSelect
                        optionsProp={listNameOptions}
                        selectedNewOptionHandler={selectedNewOptionHandler}
                    />
                </Grid>
                <button
                    className="py-2 px-4 bg-[#45185a] hover:bg-[#5e2c73] text-white rounded-md flex items-center justify-center transition-colors"
                    onClick={() => navigate(`/generate-leads`)}
                >
                    Add Leads
                </button>
            </Grid>

            <TableContainer component={Paper} sx={{ borderRadius: "8px", boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)" }}>
                <Table style={{ cursor: 'pointer' }}>
                    <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                        <TableRow>
                            <TableCell className="deal-value">
                                <div
                                    className="table-custom-header"
                                    onClick={() => handleSort('listDetails')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    List Name
                                    <img
                                        src={downArrowGrey}
                                        alt="⬇️"
                                        style={{
                                            transform: sortField === 'listDetails' && sortDirection === 'desc' ? 'rotate(180deg)' : 'none',
                                            transition: 'transform 0.2s'
                                        }}
                                    />
                                </div>
                            </TableCell>
                            <TableCell className="deal-value">
                                <div
                                    className="table-custom-header"
                                    onClick={() => handleSort('currentCompany')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Company
                                    <img
                                        src={downArrowGrey}
                                        alt="⬇️"
                                        style={{
                                            transform: sortField === 'currentCompany' && sortDirection === 'desc' ? 'rotate(180deg)' : 'none',
                                            transition: 'transform 0.2s'
                                        }}
                                    />
                                </div>
                            </TableCell>
                            <TableCell className="deal-value">
                                <div
                                    className="table-custom-header"
                                    onClick={() => handleSort('name')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Name
                                    <img
                                        src={downArrowGrey}
                                        alt="⬇️"
                                        style={{
                                            transform: sortField === 'name' && sortDirection === 'desc' ? 'rotate(180deg)' : 'none',
                                            transition: 'transform 0.2s'
                                        }}
                                    />
                                </div>
                            </TableCell>

                            <TableCell className="deal-value">
                                <div
                                    className="table-custom-header"
                                    onClick={() => handleSort('position')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Role
                                    <img
                                        src={downArrowGrey}
                                        alt="⬇️"
                                        style={{
                                            transform: sortField === 'position' && sortDirection === 'desc' ? 'rotate(180deg)' : 'none',
                                            transition: 'transform 0.2s'
                                        }}
                                    />
                                </div>
                            </TableCell>
                            <TableCell className="deal-value">
                                <div
                                    className="table-custom-header"
                                    onClick={() => handleSort('message')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    AI-Generated Message
                                    <img
                                        src={downArrowGrey}
                                        alt="⬇️"
                                        style={{
                                            transform: sortField === 'message' && sortDirection === 'desc' ? 'rotate(180deg)' : 'none',
                                            transition: 'transform 0.2s'
                                        }}
                                    />
                                </div>
                            </TableCell>
                            <TableCell className="deal-value">
                                <div
                                    className="table-custom-header"
                                    onClick={() => handleSort('createdAt')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Created At
                                    <img
                                        src={downArrowGrey}
                                        alt="⬇️"
                                        style={{
                                            transform: sortField === 'createdAt' && sortDirection === 'desc' ? 'rotate(180deg)' : 'none',
                                            transition: 'transform 0.2s'
                                        }}
                                    />
                                </div>
                            </TableCell>
                            <TableCell className="deal-value">
                                <div
                                    className="table-custom-header"
                                    style={{ cursor: 'pointer' }}
                                >
                                    Profile URL
                                </div>
                            </TableCell>
                            <TableCell className="deal-value">
                                <div
                                    className="table-custom-header"
                                    style={{ cursor: 'pointer' }}
                                >
                                    Company URL
                                </div>
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {displayData.map((row, index) => {

                            let leadId = row?._id || '';
                            let currentCompany = row?.currentCompany || '';
                            let name = row?.name || '';
                            let position = row?.position || '';
                            let message = row?.message || '';
                            let profileUrl = row?.profileUrl || '';
                            let companyUrl = row?.companyUrl || '';
                            let createdAt = row?.createdAt || '--';
                            let listName = row?.listDetails?.label || '--';

                            const boxStyle = {
                                minWidth: '110px',
                                maxWidth: '200px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }
                            return (

                                <TableRow key={index}>
                                    <ExpandableTableCell
                                        content={listName}
                                        index={`listName-${index}`}
                                        openTooltipIndex={openTooltipIndex}
                                        setOpenTooltipIndex={setOpenTooltipIndex}
                                    />
                                    <ExpandableTableCell
                                        content={currentCompany}
                                        index={`company-${index}`}
                                        openTooltipIndex={openTooltipIndex}
                                        setOpenTooltipIndex={setOpenTooltipIndex}
                                    />
                                    <ExpandableTableCell
                                        content={name}
                                        index={`name-${index}`}
                                        openTooltipIndex={openTooltipIndex}
                                        setOpenTooltipIndex={setOpenTooltipIndex}
                                    />
                                    <ExpandableTableCell
                                        content={position}
                                        index={`position-${index}`}
                                        openTooltipIndex={openTooltipIndex}
                                        setOpenTooltipIndex={setOpenTooltipIndex}
                                    />
                                    <ExpandableTableCell
                                        content={message}
                                        index={`message-${index}`}
                                        openTooltipIndex={openTooltipIndex}
                                        setOpenTooltipIndex={setOpenTooltipIndex}
                                        minWidth={135}
                                    />
                                    <TableCell className="deal-value-new">
                                        <Box sx={boxStyle}>
                                            {createdAt}
                                        </Box>
                                    </TableCell>
                                    <TableCell className="deal-value-new">
                                        <Box sx={{ minWidth: "110px", display: "flex", alignItems: "center", gap: "8px", fontWeight: 600 }}>
                                            {profileUrl
                                                ? (
                                                    <a
                                                        href={profileUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={() => handleProfileClick({
                                                            userId: user?.id,
                                                            profileUrl,
                                                            leadId
                                                        })} // Added tracking here
                                                    >
                                                        <Link sx={{ color: "#0077b5", fontSize: 24 }} />
                                                    </a>
                                                ) : '--'}
                                        </Box>
                                    </TableCell>
                                    <TableCell className="deal-value-new">
                                        <Box sx={{ minWidth: "110px", display: "flex", alignItems: "center", gap: "8px", fontWeight: 600 }}>
                                            {companyUrl
                                                ? (
                                                    <a href={companyUrl} target="_blank" rel="noopener noreferrer">
                                                        <Link sx={{ color: "#0077b5", fontSize: 24 }} />
                                                    </a>
                                                ) : '--'}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )
                        }
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default LeadsTable;